import { useIntelligence } from "../hooks/useIntelligence";
import { useJobTypeAccuracy } from "../hooks/useJobTypeAccuracy";
import { useProfileLearning } from "../ocr-health/hooks/useProfileLearning";
import { AccuracyDashboard } from "../form-config/recognition/AccuracyDashboard";
import { LearningBadge } from "../form-config/recognition/LearningBadge";
import { ReverifyValveConfig } from "../form-config/recognition/ReverifyValveConfig";
import {
  DEFAULT_REVERIFY_VALVE,
  type ReverifyValve,
} from "@capture/shared";
import "./IntelligenceTab.css";

interface IntelligenceTabProps {
  jobTypeId: string;
  verifyAutoCorrections: boolean;
  onToggleVerify: (value: boolean) => void;
  /** Current reverify valve for this job type (default: off). */
  reverifyValve?: ReverifyValve | null;
  /** Called with the validated new valve on save. */
  onSaveValve?: (next: ReverifyValve) => void;
  /** Whether a save is in progress (disables the valve save button). */
  isValveSaving?: boolean;
}

export function IntelligenceTab({
  jobTypeId,
  verifyAutoCorrections,
  onToggleVerify,
  reverifyValve,
  onSaveValve,
  isValveSaving = false,
}: IntelligenceTabProps) {
  const { status, loading, retraining, error, triggerRetrain } = useIntelligence();
  const { data: accuracy } = useJobTypeAccuracy(jobTypeId);
  // R-239 P4 Task 10 — profile learning hook for the inline field badges.
  const { forms: learningForms, available: learningAvailable, summary: learningSummary } =
    useProfileLearning(jobTypeId);

  // Flatten all fields across forms for the compact badge list.
  const allLearningFields = learningForms.flatMap((f) => f.fields);

  const effectiveValve: ReverifyValve = reverifyValve ?? DEFAULT_REVERIFY_VALVE;

  return (
    <div className="intelligence-tab">
      <section className="gt-section">
        <h3 className="rb-section-title">Auto-Correction Verification</h3>
        <label className="gt-toggle">
          <input
            type="checkbox"
            checked={verifyAutoCorrections}
            onChange={(e) => onToggleVerify(e.target.checked)}
          />
          <span>Require operator verification for auto-corrected fields</span>
        </label>
        <p className="rb-hint">
          When enabled, fields auto-corrected by the intelligence model will always require operator
          confirmation. Recommended for new forms until correction patterns stabilize.
        </p>
      </section>

      <section className="gt-section">
        <h3 className="rb-section-title">Model Status</h3>

        {loading && <p className="rb-hint">Loading intelligence status...</p>}
        {error && <p className="admin-error">{error}</p>}

        {status && (
          <>
            <div className="it-status-header">
              <span className="rb-hint">
                Last trained:{" "}
                {status.lastTrainedAt
                  ? new Date(status.lastTrainedAt).toLocaleString()
                  : "Never"}
              </span>
              <button
                onClick={triggerRetrain}
                disabled={retraining}
                className="admin-btn admin-btn--secondary"
              >
                {retraining ? "Retraining..." : "Retrain Now"}
              </button>
            </div>

            {status.vendors.length === 0 ? (
              <p className="rb-hint">
                No vendor data yet. Intelligence models activate after operators process documents
                and make corrections.
              </p>
            ) : (
              <div className="it-vendor-list">
                {status.vendors.map((v) => (
                  <div key={v.vendorId} className="it-vendor-card">
                    <div className="it-vendor-header">
                      <span className="it-vendor-name">{v.displayName}</span>
                      <span className="it-vendor-meta">{v.documentCount} documents</span>
                    </div>
                    <div className="it-stat-grid">
                      <div className="it-stat">
                        <span className="gt-label">Spatial priors</span>
                        <span className="it-stat-value">
                          {v.spatial.fieldCount} fields, avg {Math.round(v.spatial.avgSamples)} samples
                        </span>
                      </div>
                      <div className="it-stat">
                        <span className="gt-label">Value patterns</span>
                        <span className="it-stat-value">
                          {v.patterns.patternCount} patterns across {v.patterns.fieldCount} fields
                        </span>
                      </div>
                      <div className="it-stat">
                        <span className="gt-label">Co-occurrence</span>
                        <span className="it-stat-value">
                          {v.cooccurrence.pairCount} pairs,{" "}
                          {Math.round(v.cooccurrence.coveragePercent)}% high-confidence
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </section>

      <section className="gt-section">
        <h3 className="rb-section-title">Recognition Accuracy &amp; Learned Rules</h3>
        <AccuracyDashboard
          jobTypeId={jobTypeId}
          total={accuracy.total}
          corrected={accuracy.corrected}
          recent={accuracy.recent}
          averageConfidence={accuracy.averageConfidence}
          topConfused={accuracy.topConfused}
          confidenceBuckets={accuracy.confidenceBuckets}
        />
      </section>

      {/* R-239 P4 Task 10 — Field Learning badges. Sourced from the same
          useProfileLearning hook used by the OCR-health panel. Shown only
          when the OCR service reports data (available:true, fields present).
          Provides a compact per-field lifecycle view right on the job-type
          Intelligence tab without the full panel filters. */}
      {learningAvailable && allLearningFields.length > 0 && (
        <section className="gt-section">
          <h3 className="rb-section-title">Field Learning</h3>
          <p className="rb-hint">
            {learningSummary.proven} proven · {learningSummary.learning} learning ·{" "}
            {learningSummary.demoted} demoted — auto-advance rate{" "}
            {Math.round(learningSummary.autoAdvancedPct * 100)}%
          </p>
          <div className="it-field-badge-list">
            {allLearningFields.map((f) => (
              <div key={f.fieldName} className="it-field-badge-row">
                <span className="it-field-badge-name">{f.fieldName}</span>
                <LearningBadge
                  lifecycle={f.lifecycle}
                  calibratedConfidence={f.calibratedConfidence}
                  agreementCount={f.agreementCount}
                  observationCount={f.observationCount}
                  skipRate={f.skipRate}
                />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* R-239 P4 Task 10 — Re-verify valve config. Persists via the same
          handleSave → useUpdateJobType path used for verifyAutoCorrections. */}
      <section className="gt-section">
        <h3 className="rb-section-title">Re-verify Valve</h3>
        <ReverifyValveConfig
          value={effectiveValve}
          onChange={onSaveValve ?? (() => {})}
          isSaving={isValveSaving}
        />
      </section>
    </div>
  );
}
